import React from 'react';
import { hex2rgba } from 'utils/hexToRgba';
import { SocialsType } from 'types/SocialsType';
import { useRebateOptIn } from './useRebateOptIn';
import { SmsOptInType } from 'types/SmsOptInTypes';
import { validateSocials } from 'utils/validateSocials';
import { ReactComponent as OneIcon } from 'assets/icons/svg/one.svg';
import { ReactComponent as TwoIcon } from 'assets/icons/svg/two.svg';
import { ReactComponent as FourIcon } from 'assets/icons/svg/four.svg';
import { ReactComponent as FiveIcon } from 'assets/icons/svg/five.svg';
import { ReactComponent as ThreeIcon } from 'assets/icons/svg/three.svg';
import { ReactComponent as PayoutIcon } from 'assets/icons/svg/payout.svg';
import { ReactComponent as CartIcon } from 'assets/icons/svg/blue-cart.svg';
import { ReactComponent as EmailLogo } from 'assets/icons/svg/mail-colored.svg';
import { ReactComponent as ReceiptIcon } from 'assets/icons/svg/receipt-button.svg';
import { ReactComponent as SmsMessageIcon } from 'assets/icons/svg/sms-message.svg';
import { ReactComponent as Checkmark } from 'assets/icons/svg/circular-checkmark.svg';
import {
  CornerStyle,
  useThemeContext,
} from 'context/ThemeContext/ThemeContext';
import ConsentCheck from 'components/atomic/ConsentCheck';
import HtmlWrapper from 'components/wrappers/HtmlWrapper';
import SocialIcons from 'components/atomic/SocialIcons';
import TelInput from 'components/atomic/TelInput';
import Button from 'components/atomic/Button';
import Input from 'components/atomic/Input';
import RebateStatus from './RebateStatus';

type SmsOptInProps = {
  logo: boolean;
  data: SmsOptInType | null;
  socials: SocialsType;
  brandName: string;
  brandId: string;
  showRebateStatusPage?: boolean;
};

const getInstructionIcon = (iconKey: string, iconColor: string) => {
  switch (iconKey) {
    case 'one':
      return <OneIcon width={30} height={30} fill={iconColor} />;
    case 'two':
      return <TwoIcon width={30} height={30} fill={iconColor} />;
    case 'three':
      return <ThreeIcon width={30} height={30} fill={iconColor} />;
    case 'four':
      return <FourIcon width={30} height={30} fill={iconColor} />;
    case 'five':
      return <FiveIcon width={30} height={30} fill={iconColor} />;
    case 'payout':
      return <PayoutIcon width={30} height={30} fill={iconColor} />;
    case 'blue-cart':
      return <CartIcon width={30} height={30} fill={iconColor} />;
    case 'receipt-button':
      return <ReceiptIcon width={30} height={30} fill={iconColor} />;
    default:
      return (
        <div className='w-8 h-8 min-w-8 min-h-8 aspect-square rounded-full bg-gray-300 opacity-50' />
      );
  }
};

const SmsOptIn: React.FC<SmsOptInProps> = ({
  logo,
  data,
  socials,
  brandId,
  brandName,
  showRebateStatusPage = false,
}) => {
  const { menuStyle, brandTheme, buttonStyle, secondaryBrandColor } =
    useThemeContext();

  const flow = useRebateOptIn({
    data,
    brandName,
    brandId,
    showRebateStatusPage,
  });

  const getCornerStyle = (isMenuStyle: boolean, styleProperty: string) => {
    switch (styleProperty) {
      case CornerStyle.FULL_ROUND:
        return isMenuStyle ? 'rounded-3xl' : 'rounded-full';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-sm1';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return isMenuStyle ? 'rounded-3xl' : 'rounded-full';
    }
  };

  return (
    <>
      {data?.dimBackground && (
        <div className='absolute top-0 left-0 w-full h-full bg-black/50 duration-300' />
      )}
      <div
        className={`absolute top-0 left-0 w-full h-full flex flex-col justify-between items-stretch ${
          logo ? 'max-h-[calc(100%-5.25rem)] mt-[5.25rem]' : 'max-h-full mt-0'
        }`}
      >
        <div
          className={`relative w-full h-full px-5 overflow-auto 
            ${
              data?.socialMediaIcons
                ? 'max-h-[calc(100%-5.25rem)] pb-5'
                : 'max-h-full pb-6'
            } 
            ${logo ? 'pt-0' : 'pt-4'}
          `}
        >
          {flow.showRebateStatus ? (
            <RebateStatus />
          ) : (
            <div
              className={`
              ${
                data?.optInComponent?.showBackground
                  ? 'py-4 px-6 shadow-md'
                  : 'p-4 shadow-none'
              }
              ${getCornerStyle(true, menuStyle)}
            `}
              style={{
                background: data?.optInComponent?.showBackground
                  ? data?.optInComponent?.backgroundColor || '#FFFFFF'
                  : 'transparent',
              }}
            >
              {data?.instructions?.showSteps && (
                <div className='w-full relative flex flex-col items-center justify-center mt-2 mb-3 rounded-[14px] border border-solid border-primary overflow-hidden'>
                  <div className='w-full relative flex items-center justify-center px-4 py-2 bg-primary'>
                    <p className='font-semibold text-white text-lg'>
                      {data?.instructions.title}
                    </p>
                  </div>
                  <ul className='relative w-full flex flex-col items-start justify-start px-4 py-3 gap-3'>
                    {data?.instructions?.messages.map((message, index) => (
                      <li
                        key={`${message?.title}-${index}`}
                        className='w-full flex flex-row items-center justify-start gap-2'
                      >
                        <div className='flex items-center justify-center w-instructionIcon h-instructionIcon min-w-instructionIcon min-h-instructionIcon rounded-full overflow-hidden bg-primary'>
                          {getInstructionIcon(
                            message?.icon,
                            secondaryBrandColor || '#FFFFFF'
                          )}
                        </div>
                        <p className='font-medium text-darkGray text-xs'>
                          {message?.title}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <HtmlWrapper
                html={data?.optInComponent?.content}
                background='transparent'
                styles='!mb-2'
              />
              {flow.currentScreen === 'sms' ? (
                <>
                  {flow.showTextSentSuccess && (
                    <div
                      className={`flex flex-row items-center justify-start px-3 py-1.5 gap-3 mb-1.5 ${getCornerStyle(
                        false,
                        buttonStyle
                      )}`}
                      style={{ background: hex2rgba(brandTheme, 0.1) }}
                    >
                      <Checkmark />
                      <p className='font-medium text-xxs text-primary [&_span]:underline [&_span]:cursor-pointer'>
                        {flow.t('messageSentText')}
                        <span onClick={() => window.location.reload()}>
                          {flow.t('trySendingAgain')}
                        </span>
                        .
                      </p>
                    </div>
                  )}
                  {flow.showVerifyPhoneHeader && (
                    <div className='relative w-full flex flex-col items-center justify-center mt-2 mb-3 gap-1.5'>
                      <p className='text-base font-semibold text-center'>
                        {flow.t('verifyPhoneNumberTitle')}
                      </p>
                      <p className='font-semibold text-xs text-center text-muted tracking-[0.3px]'>
                        {flow.verifyPhoneDescription}
                      </p>
                    </div>
                  )}
                  {flow.showOtpEntry && (
                    <div className='relative w-full flex flex-col items-center justify-center mt-2 mb-3 gap-1.5'>
                      <p
                        className={`text-base font-semibold text-center ${flow.bgTextColor}`}
                      >
                        {flow.t('otpConfirmPhoneHeading')}
                      </p>
                      <p className='font-semibold text-xs text-center text-muted tracking-[0.3px] text-muted'>
                        {flow
                          .t('otpPasscodeSentMessage')
                          .replace('{{phoneNumber}}', flow.otpPhoneDisplay)}
                      </p>
                    </div>
                  )}
                  {flow.showSmsConsentCheck && (
                    <ConsentCheck
                      styles={flow.consentMargin}
                      enabled={[true]}
                      errors={[flow.consentError]}
                      consentChecks={[flow.smsConsentChecked]}
                      toggleConsentChecks={[flow.toggleSmsConsentChecked]}
                      texts={[flow.smsConsentText]}
                    />
                  )}
                  <div
                    className={`w-full px-0.5 duration-200 overflow-hidden ${
                      flow.numberField ? 'h-0' : 'h-[58px]'
                    }`}
                  >
                    <Button
                      variant='dark'
                      disabled={flow.isSmsConsentBlocking}
                      styles='!text-sm !px-12 !mt-1 !duration-200'
                      title={flow.textUsButtonLabel}
                      onClick={() => flow.openSMSApp()}
                      icon={
                        <SmsMessageIcon
                          width={32}
                          fill={brandTheme}
                          className='absolute left-3.5'
                        />
                      }
                    />
                  </div>
                  <div
                    className={`
                    relative w-full flex flex-col px-0.5 overflow-hidden 
                    ${flow.textSent ? 'duration-0' : 'duration-200'} 
                    ${flow.inputAreaHeight}`}
                  >
                    {flow.numberField ? (
                      <>
                        {!flow.textSent && (
                          <>
                            {flow.showOtpEntry ? (
                              <>
                                <div className='flex items-center justify-center gap-1.5 mb-1'>
                                  {Array.from({ length: 6 }).map((_, index) => (
                                    <input
                                      key={index}
                                      ref={(element) => {
                                        flow.otpInputRefs.current[index] =
                                          element;
                                      }}
                                      type='text'
                                      inputMode='numeric'
                                      pattern='[0-9]*'
                                      maxLength={1}
                                      autoFocus={index === 0}
                                      autoComplete={
                                        index === 0 ? 'one-time-code' : 'off'
                                      }
                                      value={flow.otpCode[index] || ''}
                                      onChange={(event) =>
                                        flow.handleOtpDigitChange(
                                          index,
                                          event.target.value
                                        )
                                      }
                                      onPaste={flow.handleOtpPaste}
                                      onKeyDown={(event) =>
                                        flow.handleOtpKeyDown(index, event)
                                      }
                                      onFocus={() => flow.handleOtpFocus(index)}
                                      className='w-[40px] h-[52px] rounded-lg text-center text-base font-semibold border border-solid border-[#98A3AA] focus:border-primary outline-none transition-colors duration-200 bg-white'
                                      style={{ padding: '10px' }}
                                    />
                                  ))}
                                </div>
                                <Button
                                  variant='dark'
                                  loading={flow.otpLoading || flow.loading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => flow.handleOtpSubmit()}
                                  title={flow.t('otpVerifyButton')}
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                                <Button
                                  variant='link'
                                  disabled={flow.otpLoading}
                                  title={flow.t('otpResendButton')}
                                  onClick={() => flow.handleResendOtp()}
                                  styles={`!text-sm !h-10 !mt-3 !mb-1 ${flow.bgTextColor}`}
                                />
                              </>
                            ) : flow.existingPhone ? (
                              <>
                                <Button
                                  variant='dark'
                                  disabled={flow.isSmsConsentBlocking}
                                  loading={flow.loading || flow.otpLoading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => flow.handlePhoneSubmit()}
                                  title={flow.existingPhoneButtonLabel}
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <TelInput
                                  autoFocus={true}
                                  value={flow.textNumber}
                                  mask='(+1) 999 999 9999'
                                  name='phone-number-input'
                                  styles='!h-input !flex-shrink-0 mt-1'
                                  placeholder={flow.t(
                                    'phoneNumberInputPlaceholder'
                                  )}
                                  onChange={(event) =>
                                    flow.setTextNumber(event.target.value)
                                  }
                                />
                                <Button
                                  variant='dark'
                                  disabled={flow.isSmsConsentBlocking}
                                  loading={flow.loading || flow.otpLoading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => flow.handlePhoneSubmit()}
                                  title={flow.phoneSubmitButtonLabel}
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                                {flow.showTextUsLink ? (
                                  <Button
                                    variant='link'
                                    title={flow.t('sendUsText')}
                                    onClick={() =>
                                      flow.toggleNumberField(false)
                                    }
                                    styles={`!text-sm !h-8 mt-2 ${flow.bgTextColor}`}
                                  />
                                ) : null}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Button
                        variant='link'
                        title={flow.t('sendMeText')}
                        onClick={() => flow.toggleNumberField(true)}
                        styles={`!text-sm !h-8 mt-0.5 ${flow.bgTextColor}`}
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {data?.optInComponent?.emailConsentBox && (
                      <ConsentCheck
                        styles={flow.consentMargin}
                        enabled={[true]}
                        errors={[flow.consentError]}
                        consentChecks={[flow.emailConsentChecked]}
                        texts={[flow.emailConsentText]}
                        toggleConsentChecks={[flow.toggleEmailConsentChecked]}
                      />
                    )}
                    <Input
                      autoFocus
                      type='email'
                      name='reabte-email'
                      value={flow.rebateEmail}
                      autoComplete='email'
                      className='rebate-email-input'
                      aria-label={flow.t('ariaRebateEmailInput')}
                      styles='!h-input !flex-shrink-0 !mt-3 !bg-white'
                      onChange={(event) =>
                        flow.setRebateEmail(event.target.value)
                      }
                      placeholder={flow.emailPlaceholder}
                    />
                    <Button
                      variant='dark'
                      title={flow.t('submitButtonText')}
                      disabled={flow.isEmailConsentBlocking}
                      loading={flow.loading || flow.checkingEmail}
                      styles='!text-sm !px-12 !my-3 !duration-200'
                      onClick={() => flow.handleRebateEmailSubmission(false)}
                      icon={
                        <EmailLogo
                          width={32}
                          fill='#FFFFFF'
                          className='absolute left-3.5'
                        />
                      }
                    />
                    {flow.showEmailSkipButton &&
                      (flow.loading ? (
                        <div className='relative w-full !h-8 !mb-3 bg-transparent' />
                      ) : (
                        <Button
                          variant='link'
                          title={flow.t('skipButtonText')}
                          onClick={() => flow.handleRebateEmailSubmission(true)}
                          styles={`!text-sm !h-8 !mb-3 ${flow.bgTextColor}`}
                        />
                      ))}
                  </div>
                </>
              )}
              {flow.showEmailImplicitLegal && (
                <HtmlWrapper
                  html={flow.emailConsentText}
                  background='transparent'
                  styles='px-2.5 [&_p]:!text-xxxs [&_p]:!leading-[12px] text-justify [&_p]:!opacity-50'
                  paragraphTextColor={flow.legalParagraphColor}
                />
              )}
              {flow.showOtpLegal && (
                <HtmlWrapper
                  background='transparent'
                  html={flow.otpLegalHtml}
                  styles='px-2.5 [&_p]:!text-xxxs [&_p]:!leading-[12px] text-justify [&_p]:!opacity-50'
                />
              )}
              {flow.showSmsImplicitLegal && (
                <HtmlWrapper
                  background='transparent'
                  html={flow.smsConsentText}
                  styles='px-2.5 [&_p]:!text-xxxs [&_p]:!leading-[12px] text-justify [&_p]:!opacity-50'
                  paragraphTextColor={flow.legalParagraphColor}
                />
              )}
            </div>
          )}
        </div>
        {data?.socialMediaIcons && validateSocials(socials) && (
          <div className='relative w-full h-[5.688rem] flex items-center bg-transparent border-solid border-0 border-t border-white/20'>
            <SocialIcons socials={socials} darkIcons />
          </div>
        )}
      </div>
    </>
  );
};

export default SmsOptIn;
